import './CourseOutline.scss'

import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Accordion } from 'react-bootstrap'
import parse from 'html-react-parser'
import {
  LMS_TYPE_TOPIC,
  isFreeCourse,
  tryGetCourseField,
  tryGetOrderedTopicsForLessonFromCourse,
  tryGetTopicCountForLessonFromCourseSteps
} from 'components/common/api/Courses'
import {
  tryGetStatusForCourseObject,
  tryGetTopicCompletedCountFromCourseProgress,
  STATUS_COMPLETED,
  getCourseProgress
} from 'components/common/api/CourseProgress'
import { getTopicUrl } from 'components/common/helpers/Paths'
import { navigateTo } from 'components/common/navigation'
import ProgressBar from 'components/common/progress/ProgressBar'
import { capitalize } from 'components/common/helpers/Strings'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import cn from 'classnames'
import Bookmark from 'components/bookmark/Bookmark'

/**
 * Course outline component. Used for displaying a Course outline, or table of contents.
 */
export default function CourseOutline({
  course,
  setCourseDetailProgress,
  triggerRender
}) {
  const history = useHistory()
  const { userSubscription: subscription, userHasPremiumAccess } =
    useContext(SubscriptionContext)
  const [courseProgress, setCourseProgress] = useState([])
  const [courseIsLocked, setCourseIsLocked] = useState(true)

  // Subscription hook
  useEffect(() => {
    // Return early if we don't have course or subscription data
    if (!course || !subscription) return
    const isFree = isFreeCourse(course)
    const isLocked = !userHasPremiumAccess && !isFree
    setCourseIsLocked(isLocked)
  }, [course, subscription, userHasPremiumAccess])

  // Course detail hook
  useEffect(() => {
    // Return early if we don't have course data
    if (!course) return

    async function getCourseProgressAsync() {
      // NOTE: The CourseContext contains a progress overview, so we need to get the
      // full progress details for the course and all its steps
      const progress = await getCourseProgress(course.id)
      setCourseProgress(progress)

      if (setCourseDetailProgress) setCourseDetailProgress(progress)
    }
    getCourseProgressAsync()
  }, [course, setCourseDetailProgress, triggerRender])

  return (
    <>
      {course &&
        course.sections &&
        course.sections.map(section => (
          <div
            key={section.ID}
            className={cn('course-section', { locked: !courseIsLocked })}
          >
            <Accordion
              className='position-relative'
              defaultActiveKey='0'
              alwaysOpen
            >
              {section.lessons &&
                section.lessons.map((lesson, lessonIndex) => {
                  // Wrap the map items in a try/catch to prevent the entire render from failing if we have a bad node
                  try {
                    // Lesson duration
                    let duration = tryGetCourseField(lesson, 'video_duration')
                    duration = duration ? Math.round(parseInt(duration, 10)) : 0

                    // Lesson status and course progress
                    const lessonStatus = tryGetStatusForCourseObject(
                      courseProgress,
                      lesson
                    )

                    // Has the user already completed this lesson?
                    const lessonIsCompleted = lessonStatus === STATUS_COMPLETED

                    // Get topics from steps data
                    let topics = tryGetOrderedTopicsForLessonFromCourse(
                      lesson.id,
                      course
                    )

                    // Get total topic count
                    const lessonTopicsTotal =
                      tryGetTopicCountForLessonFromCourseSteps(
                        lesson.id,
                        course.steps
                      ) || 1 // Null coalesce to 1

                    // Get topics completed
                    const lessonTopicsCompleted =
                      tryGetTopicCompletedCountFromCourseProgress(
                        lesson.id,
                        courseProgress,
                        course.steps
                      )

                    // If the lesson is completed, let's just mark it complete
                    const percentComplete = lessonIsCompleted
                      ? 100
                      : Math.round(
                          (lessonTopicsCompleted / lessonTopicsTotal) * 100
                        ) || 0 // Null coalesce to 0

                    return (
                      lesson.id && (
                        <Accordion.Item
                          id={lesson.id}
                          key={lessonIndex}
                          // eventKey must be a string
                          eventKey={`${lessonIndex}`}
                          className={lessonStatus}
                        >
                          <Accordion.Header
                            className='lesson-heading'
                            as={'div'}
                          >
                            <div className='d-flex flex-column justify-content-between'>
                              <div className='lesson-detail d-flex justify-content-between'>
                                <span className='lesson-day mb-2'>
                                  Session {lessonIndex + 1}
                                </span>
                                <span className='mb-0'>
                                  {duration > 0 && (
                                    <span className='lesson-duration'>
                                      {duration}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className='lesson-title d-flex justify-content-between'>
                                <span className='mb-0'>
                                  {lesson.clean_title}
                                </span>
                              </div>
                            </div>
                            {!courseIsLocked && (
                              <span className='lesson-progress'>
                                <ProgressBar percent={percentComplete} />
                                {percentComplete}% of {lessonTopicsTotal}
                                &nbsp; sessions
                              </span>
                            )}
                          </Accordion.Header>
                          <Accordion.Body>
                            {topics?.map((topic, topicIndex) => {
                              // Get topic status and progress
                              const topicStatus = tryGetStatusForCourseObject(
                                courseProgress,
                                topic
                              )

                              // Video details
                              const videoDuration = tryGetCourseField(
                                topic,
                                'video_duration'
                              )

                              return (
                                topic && (
                                  <div
                                    key={topicIndex}
                                    className={cn('lesson-topic', {
                                      'no-duration': !videoDuration
                                    })}
                                    onClick={_ =>
                                      navigateTo(
                                        getTopicUrl(
                                          courseIsLocked,
                                          course.id,
                                          lesson.id,
                                          topic
                                        ),
                                        history
                                      )
                                    }
                                  >
                                    <h5 className='lesson-topic-title'>
                                      <span className='lesson-topic-counter'>
                                        {topicIndex + 1}.
                                      </span>
                                      {topic?.clean_title &&
                                        parse(topic.clean_title)}
                                    </h5>

                                    {/* Bookmark */}
                                    <Bookmark
                                      category={LMS_TYPE_TOPIC}
                                      categoryObjectId={topic.id}
                                      color='green'
                                    />

                                    {/* Status */}
                                    {topicStatus && (
                                      <span
                                        className={cn('lesson-topic-status', {
                                          [topicStatus]: true
                                        })}
                                      >
                                        {/* Show "In Progress" instead of "not-started" */}
                                        {capitalize(
                                          topicStatus === 'not-started'
                                            ? 'in progress'
                                            : topicStatus
                                        )}
                                      </span>
                                    )}

                                    {/* Duration */}
                                    {videoDuration && (
                                      <span className='lesson-topic-duration'>
                                        {videoDuration}
                                      </span>
                                    )}
                                  </div>
                                )
                              )
                            })}
                          </Accordion.Body>
                        </Accordion.Item>
                      )
                    )
                  } catch (e) {
                    // Send the exception to the console
                    console.error(e)

                    return (
                      <Accordion.Item eventKey='error'>
                        <Accordion.Body>Error</Accordion.Body>
                      </Accordion.Item>
                    )
                  }
                })}
            </Accordion>
            {courseIsLocked && course.sections?.length > 0 && (
              <div className='locked-overflow d-flex justify-content-center align-items-center'>
                <div className='locked-overflow-content gap-2 d-flex justify-content-around align-items-start'>
                  <i className='bi bi-lock'></i>
                  <span>Members only</span>
                </div>
              </div>
            )}
          </div>
        ))}
    </>
  )
}
